import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { createAdmin } from "../../../core/services/adminService";
import { canManageAdmins } from "../../../core/adminAccess";
import {
  AdminAccountFields,
  AdminFormState,
  PermissionPicker,
  createEmptyAdminForm,
} from "./adminFormShared";
import { getEnabledPermissionKeys } from "../../../core/adminPermissions";

const AdminAdd = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<AdminFormState>(createEmptyAdminForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!canManageAdmins()) {
      navigate("/admins", { replace: true });
    }
  }, [navigate]);

  const updateForm = (updates: Partial<AdminFormState>) => {
    setForm((current) => ({ ...current, ...updates }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (getEnabledPermissionKeys(form.permissions).length === 0) {
      Swal.fire("Permission required", "Select at least one module permission.", "warning");
      return;
    }

    setSaving(true);

    try {
      const result = await createAdmin({
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password.trim(),
        role: form.role,
        isActive: form.isActive,
        permissions: form.permissions,
      });

      if (!result.success) throw new Error(result.message || "Unable to create admin");

      await Swal.fire({
        title: "Admin Created",
        text: "Admin account created successfully.",
        icon: "success",
        timer: 1400,
        showConfirmButton: false,
      });

      navigate(result.admin?._id ? `/admins/${result.admin._id}` : "/admins");
    } catch (error: any) {
      Swal.fire("Create Failed", error.message || "Unable to create admin", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="detail-page">
      <div className="page-hdr">
        <div className="page-hdr-left">
          <Link to="/admins" className="btn-sm btn-outline back-link">
            ← Back to All Admins
          </Link>
          <div className="page-eyebrow">Access Control</div>
          <h1 className="page-title">
            Add New <em>Admin</em>
          </h1>
          <div className="page-sub">
            Create a manager or support account for the admin panel.
          </div>
        </div>
        <div className="page-hdr-right">
          <button type="submit" form="admin-add-form" className="btn-sm btn-primary" disabled={saving}>
            {saving ? "Creating..." : "Create Admin"}
          </button>
        </div>
      </div>

      <form id="admin-add-form" onSubmit={handleSubmit} className="page-stack">
        <section className="table-card">
          <div className="table-head">
            <h2>Account Details</h2>
            <p>Basic login information and role assignment</p>
          </div>
          <div className="admin-card-body">
            <AdminAccountFields form={form} onChange={updateForm} />
          </div>
        </section>

        <PermissionPicker permissions={form.permissions} onChange={(permissions) => updateForm({ permissions })} />

        <div className="detail-footer">
          <Link to="/admins" className="btn-sm btn-outline">
            Cancel
          </Link>
          <div className="detail-footer-right">
            <button type="submit" className="btn-sm btn-primary" disabled={saving}>
              {saving ? "Creating..." : "Create Admin"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AdminAdd;
